import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { BotAction } from '../types';
import type { ActionTestResult, RehearsalOutput } from '../api';
import { JsonTree, type Pick } from './JsonTree';
import { actionConfig, placeholdersIn, sampleFor } from './placeholders';

export type TestAction = (id: number, body: { variables?: Record<string, unknown>; body?: unknown; live?: boolean }) => Promise<ActionTestResult>;

const FIELD = 'mt-1 w-full rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body focus-visible:outline-none focus-visible:border-(--color-brand)';
const PRIMARY = 'rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-(--color-brand-ink) transition hover:bg-(--color-brand-strong) disabled:opacity-40';

/**
 * One call, made from here, and its answer on screen.
 *
 * Asks for exactly the `{{values}}` the address and body need — one box each,
 * pre-filled with something plausible — instead of "paste the variables as JSON".
 * Runs through the rehearsal, so the URL is built, the credential attached and the
 * answer read by the code that runs for real customers. Reads run for real; a write
 * only when the box is ticked. The answer is a tree; with `onPick`, clicking a field
 * is how the step learns what to remember.
 */
export function TryAction({ action, body, test, onPick, pickLists = false, pickValues = true, pickHint, picked, showAddress = true }: {
  action: BotAction;
  /** The step's request body, if any — its placeholders are asked for too. */
  body?: unknown;
  test: TestAction;
  onPick?: (pick: Pick) => void;
  pickLists?: boolean;
  pickValues?: boolean;
  pickHint?: string;
  picked?: Record<string, string>;
  showAddress?: boolean;
}) {
  const config = useMemo(() => actionConfig(action), [action]);
  const isRead = (config.method ?? 'POST').toUpperCase() === 'GET';
  const names = useMemo(() => placeholdersIn(config.url, config.headers, body), [config, body]);
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(names.map((n) => [n, sampleFor(n)])));
  const [live, setLive] = useState(false);
  const [result, setResult] = useState<RehearsalOutput | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () => test(action.id, {
      variables: Object.fromEntries(names.map((n) => [n, values[n] ?? ''])),
      body,
      live: !isRead && live,
    }),
    onSuccess: (r) => { setProblem(null); setResult(r.call); },
    onError: (e) => { setProblem(e instanceof Error ? e.message : 'That did not run.'); setResult(null); },
  });

  const failed = result ? result.status === 0 || (result.status ?? 0) >= 400 : false;

  return (
    <div className="grid gap-3">
      {showAddress && (
        <p className="font-mono text-small break-all text-(--color-muted)">
          {config.method ?? 'POST'} {config.url}
        </p>
      )}

      {names.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {names.map((name) => (
            <label key={name} className="block">
              <span className="font-mono text-small font-medium text-(--color-muted)">{`{{${name}}}`}</span>
              <input value={values[name] ?? ''} onChange={(e) => setValues({ ...values, [name]: e.target.value })} className={FIELD} placeholder="A value to try with" />
            </label>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => run.mutate()} disabled={run.isPending} className={PRIMARY}>
          {run.isPending ? 'Calling…' : result ? 'Call it again' : 'Call it'}
        </button>
        {isRead ? (
          <span className="text-small text-(--color-muted)">A lookup runs for real.</span>
        ) : (
          <label className="flex items-center gap-2 text-small">
            <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} className="size-4 accent-(--color-brand)" />
            <span className={live ? 'text-(--color-bad)' : 'text-(--color-muted)'}>
              {live ? 'For real — this will write to your system' : 'Only build the request; do not send it'}
            </span>
          </label>
        )}
      </div>

      {problem && <p className="text-small text-(--color-bad)">{problem}</p>}

      {result && (
        <div className={`rounded-lg border px-3 py-2.5 ${failed ? 'border-(--color-bad) bg-(--color-bad-wash)' : 'border-(--color-line) bg-(--color-surface)'}`}>
          <div className="flex flex-wrap items-center gap-2 text-small">
            <span className={`font-semibold ${failed ? 'text-(--color-bad)' : 'text-(--color-good)'}`}>
              {result.simulated ? 'Not sent' : result.status === 0 ? 'Could not be reached' : `Answered ${result.status}`}
            </span>
            {result.url && <span className="min-w-0 truncate font-mono text-(--color-muted)">{result.url}</span>}
          </div>
          {result.note && <p className="mt-1 text-small text-(--color-muted)">{result.note}</p>}
          {!result.simulated && result.status !== 0 && (
            <div className="mt-2">
              {onPick && pickHint && <p className="mb-1.5 text-small text-(--color-muted)">{pickHint}</p>}
              {!onPick && (
                <p className="mb-1.5 text-small text-(--color-muted)">
                  This is what a bot would get back. To remember any of these fields, open a bot, add a
                  <span className="font-medium text-(--color-ink)"> Call a system</span> step, choose this action and click the field there.
                </p>
              )}
              <div className="pane-scroll max-h-72 overflow-auto rounded-lg bg-(--color-paper) p-2">
                <JsonTree value={result.data} onPick={onPick} pickLists={pickLists} pickValues={pickValues} picked={picked} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
